"use client"

import React from "react"
import type {InsightEvidence} from "@devplaninc/codeup"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {EvidenceProviderIcon} from "@/app/components/evidence/EvidenceProviderIcon"
import {evidenceProviderLabel} from "@/app/components/evidence/evidenceProvider"
import {
  aggregateEvidenceProviderCounts,
  type EvidenceProviderCount,
} from "@/app/components/evidence/evidenceProviderCounts"
import {EvidenceDetailFlyout} from "@/app/components/evidence/EvidenceDetailFlyout"
import {
  displayableEvidence,
  evidenceProviders,
  type EvidenceResolvers,
} from "@/app/components/evidence/evidenceDisplay"
import {usePreferredGitProvider} from "@/hooks/queries/use-integrations"
import {cn} from "@/lib/utils"

const DEFAULT_MAX_STACKED_ICONS = 3

function providerCountsTooltipLabel(counts: EvidenceProviderCount[]): string {
  return counts
    .map(entry => `${evidenceProviderLabel(entry.provider)} x${entry.count}`)
    .join(", ")
}

function formatSourceCountLabel(count: number): string {
  return `${count} source${count === 1 ? "" : "s"}`
}

export function evidenceSourcesPillLabel(evidence: InsightEvidence[]): string {
  const visibleEvidence = displayableEvidence(evidence)
  return formatSourceCountLabel(visibleEvidence.length)
}

export function EvidenceSourcesPill({
  evidence,
  companyId,
  resolvers,
  title = "Sources",
  maxStackedIcons = DEFAULT_MAX_STACKED_ICONS,
  className,
  onClick,
  compact = false,
  defaultOpen = false,
}: {
  evidence: InsightEvidence[]
  companyId?: number
  resolvers?: EvidenceResolvers
  title?: string
  maxStackedIcons?: number
  className?: string
  onClick?: () => void
  compact?: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  React.useEffect(() => {
    if (defaultOpen) setOpen(true)
  }, [defaultOpen])
  const visibleEvidence = React.useMemo(() => displayableEvidence(evidence), [evidence])
  const {preferredGitProvider} = usePreferredGitProvider(companyId)
  const pillResolvers = React.useMemo(
    () => preferredGitProvider === undefined ? resolvers : {...resolvers, preferredGitProvider},
    [preferredGitProvider, resolvers],
  )
  const counts = React.useMemo(
    () => aggregateEvidenceProviderCounts(evidenceProviders(visibleEvidence, pillResolvers)),
    [visibleEvidence, pillResolvers],
  )
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (companyId !== undefined) setOpen(true)
    onClick?.()
  }, [companyId, onClick])

  if (visibleEvidence.length === 0) return null

  const stackedIcons = counts.slice(0, maxStackedIcons)
  const hiddenIconCount = counts.length - stackedIcons.length
  const sourceCount = visibleEvidence.length
  const tooltip = counts.length > 0 ? providerCountsTooltipLabel(counts) : formatSourceCountLabel(sourceCount)
  const pillLabel = evidenceSourcesPillLabel(visibleEvidence)
  const pillButton = (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Open ${title.toLowerCase()}: ${pillLabel}`}
      className={cn(
        compact
          ? "mx-px inline-flex shrink-0 cursor-pointer items-center gap-[4px] rounded-full border border-border/60 bg-background py-[2px] pl-[5px] pr-[7px] align-baseline text-[11px] font-medium leading-[15px] text-foreground shadow-none transition-colors hover:border-border hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          : "inline-flex h-7 shrink-0 items-center gap-2 rounded-full border border-border bg-background px-2.5 text-xs text-muted-foreground shadow-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <span className="flex items-center">
        {stackedIcons.length > 0 ? stackedIcons.map((entry, index) => (
          <span
            key={entry.provider}
            className={cn(
              "relative rounded-full ring-background",
              compact ? "ring-1" : "ring-2",
              index > 0 && (compact ? "-ml-1" : "-ml-2"),
            )}
          >
            <EvidenceProviderIcon provider={entry.provider} size={compact ? "xxs" : "xs"}/>
          </span>
        )) : (
          <EvidenceProviderIcon provider="unknown" size={compact ? "xxs" : "xs"}/>
        )}
        {hiddenIconCount > 0 && (
          <span className={cn(
            "relative inline-flex items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ring-background",
            compact ? "-ml-1 size-3 text-[7px] ring-1" : "-ml-2 size-5 text-[9px] ring-2",
          )}>
            +{hiddenIconCount}
          </span>
        )}
      </span>
      <span className={compact ? "max-w-[160px] truncate whitespace-nowrap" : "whitespace-nowrap"}>
        {pillLabel}
      </span>
    </button>
  )

  return (
    <>
      {compact ? pillButton : (
        <Tooltip>
          <TooltipTrigger asChild>{pillButton}</TooltipTrigger>
          <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
      )}

      {companyId !== undefined && (
        <EvidenceDetailFlyout
          evidence={visibleEvidence}
          companyId={companyId}
          open={open}
          onClose={() => setOpen(false)}
          resolvers={pillResolvers}
          title={title}
        />
      )}
    </>
  )
}
